import { useState } from 'react'
import { Heading, Text, Button } from '@shinkansen/sakura'
import { useAuth } from '../auth'
import { useChangePassword } from '../api/account'
import styles from './account.module.css'

export function AccountPage() {
  const { user } = useAuth()
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  const changePassword = useChangePassword()

  const [errorMessage, setErrorMessage] = useState('')

  const handleSubmitPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setSuccessMessage('')
    setErrorMessage('')

    // Client-side validation: check passwords match before API call
    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match')
      return
    }

    const result = await changePassword.mutateAsync({
      currentPassword,
      newPassword,
      confirmPassword,
    })

    if (result.success) {
      setSuccessMessage('Password changed successfully')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setShowPasswordForm(false)
    }
  }

  const handleCancelPassword = () => {
    setShowPasswordForm(false)
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setErrorMessage('')
    changePassword.reset()
  }

  if (!user) {
    return (
      <div className={styles.page}>
        <div className={styles.empty}>
          <Text>Please log in to view your account settings.</Text>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Heading size="xl">ACCOUNT SETTINGS</Heading>
      </header>

      {successMessage && (
        <div className={styles.successBanner}>{successMessage}</div>
      )}

      {/* Profile Section */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionNumber}>01</span>
          <h2 className={styles.sectionTitle}>PROFILE</h2>
        </div>

        <div className={styles.card}>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>NAME</span>
            <span className={styles.fieldValue}>{user.name || 'Not set'}</span>
          </div>

          <div className={styles.divider} />

          <div className={styles.field}>
            <span className={styles.fieldLabel}>EMAIL</span>
            <span className={styles.fieldValue}>{user.email}</span>
          </div>

          <div className={styles.divider} />

          <div className={styles.field}>
            <span className={styles.fieldLabel}>USER ID</span>
            <span className={styles.fieldValueMono}>{user.id}</span>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionNumber}>02</span>
          <h2 className={styles.sectionTitle}>SECURITY</h2>
        </div>

        <div className={styles.card}>
          {!showPasswordForm ? (
            <div className={styles.securityItem}>
              <div className={styles.securityInfo}>
                <span className={styles.securityLabel}>PASSWORD</span>
                <span className={styles.securityDesc}>
                  Change your account password
                </span>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowPasswordForm(true)}
              >
                CHANGE PASSWORD
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmitPassword} className={styles.passwordForm}>
              {(errorMessage || changePassword.data?.error) && (
                <div className={styles.errorBanner}>{errorMessage || changePassword.data?.error}</div>
              )}

              <div className={styles.formField}>
                <label className={styles.formLabel}>CURRENT PASSWORD</label>
                <input
                  type="password"
                  className={styles.input}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>

              <div className={styles.formField}>
                <label className={styles.formLabel}>NEW PASSWORD</label>
                <input
                  type="password"
                  className={styles.input}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
                <span className={styles.fieldHint}>Minimum 8 characters</span>
              </div>

              <div className={styles.formField}>
                <label className={styles.formLabel}>CONFIRM NEW PASSWORD</label>
                <input
                  type="password"
                  className={styles.input}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </div>

              <div className={styles.formActions}>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleCancelPassword}
                >
                  CANCEL
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={changePassword.isPending}
                >
                  {changePassword.isPending ? 'SAVING...' : 'SAVE PASSWORD'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Roles Section (if user has roles) */}
      {user.roles && user.roles.length > 0 && (
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionNumber}>03</span>
            <h2 className={styles.sectionTitle}>ROLES</h2>
          </div>

          <div className={styles.card}>
            <div className={styles.rolesList}>
              {user.roles.map((role) => (
                <span key={role} className={styles.roleBadge}>
                  {role.replace(/_/g, ' ').toUpperCase()}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  )
}
